import React from "react";
import { useSearchParams } from "react-router-dom";
import type { BankAccount } from "@/hooks/use-bank-accounts";
import { useUnmatchedBankTransactions } from "@/hooks/finance/reconciliation/matching-hooks/useUnmatchedBankTransactions";
import { useUnmatchedPaidExpenses } from "@/hooks/finance/reconciliation/matching-hooks/useUnmatchedPaidExpenses";
import { useMatchedBankTransactions } from "@/hooks/finance/reconciliation/matching-hooks/useMatchedBankTransactions";
import { useMatchedPaidExpenses } from "@/hooks/finance/reconciliation/matching-hooks/useMatchedPaidExpenses";
import { fmtDate } from "./matching-tabs/matching-ui";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  TrendingDown,
  TrendingUp,
  Banknote,
  Receipt,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

type TabKey = "unmatched-bank" | "unmatched-paid" | "matched-bank" | "matched-paid";

type Props = {
  dateFrom: string;
  dateTo: string;
  bankAccountId?: string | null;
  accounts: BankAccount[];
};

const toNum = (v: unknown) => {
  const n = typeof v === "string" ? parseFloat(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

function summarize<T extends Record<string, any>>(
  rows: T[],
  amountKeys: string[],
  currencyKey: string,
  fallbackCurrency = "ETB"
) {
  let total = 0;
  const byCurrency = new Map<string, number>();
  for (const r of rows) {
    let amt = 0;
    for (const k of amountKeys) {
      if (r?.[k] != null) {
        amt = Math.abs(toNum(r[k]));
        break;
      }
    }
    total += amt;
    const cur = (r?.[currencyKey] as string) || fallbackCurrency;
    byCurrency.set(cur, (byCurrency.get(cur) ?? 0) + amt);
  }
  const currencies = Array.from(byCurrency.keys());
  const displayCurrency =
    currencies.length === 1 ? currencies[0] : currencies.length === 0 ? fallbackCurrency : "Mixed";
  return { count: rows.length, total, displayCurrency, byCurrency };
}

function groupByBank<T extends Record<string, any>>(
  rows: T[],
  amountKeys: string[],
  currencyKey: string,
  accountNameById: Map<string, string>,
  fallbackCurrency = "ETB"
) {
  const map = new Map<
    string,
    { count: number; amount: number; byCurrency: Map<string, number> }
  >();
  for (const r of rows) {
    const id = (r.bank_account_id as string) || "__none__";
    let amt = 0;
    for (const k of amountKeys) {
      if (r?.[k] != null) {
        amt = Math.abs(toNum(r[k]));
        break;
      }
    }
    const cur = (r?.[currencyKey] as string) || fallbackCurrency;
    const cur0 = map.get(id);
    if (cur0) {
      cur0.count += 1;
      cur0.amount += amt;
      cur0.byCurrency.set(cur, (cur0.byCurrency.get(cur) ?? 0) + amt);
    } else {
      const bc = new Map<string, number>();
      bc.set(cur, amt);
      map.set(id, { count: 1, amount: amt, byCurrency: bc });
    }
  }
  return Array.from(map.entries())
    .map(([id, v]) => {
      const currencies = Array.from(v.byCurrency.keys());
      const displayCurrency =
        currencies.length === 1
          ? currencies[0]
          : currencies.length === 0
          ? fallbackCurrency
          : "Mixed";
      return {
        id,
        name:
          id === "__none__"
            ? "Unassigned"
            : accountNameById.get(id) ?? "Unknown account",
        count: v.count,
        amount: v.amount,
        displayCurrency,
        byCurrency: v.byCurrency,
      };
    })
    .sort((a, b) => b.count - a.count);
}

function MetricTile({
  label,
  count,
  total,
  currency,
  byCurrency,
  icon,
  tone,
  isLoading,
  onClick,
  active,
}: {
  label: string;
  count: number;
  total: number;
  currency: string;
  byCurrency: Map<string, number>;
  icon: React.ReactNode;
  tone: "warn" | "ok" | "neutral";
  isLoading: boolean;
  onClick: () => void;
  active: boolean;
}) {
  const toneClasses =
    tone === "warn"
      ? "border-amber-200 bg-amber-50/50 hover:bg-amber-50"
      : tone === "ok"
      ? "border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50"
      : "border-slate-200 bg-white hover:bg-slate-50";

  const ringClasses = active
    ? tone === "warn"
      ? "ring-2 ring-amber-400"
      : "ring-2 ring-emerald-400"
    : "";

  const iconTone =
    tone === "warn" ? "text-amber-600" : tone === "ok" ? "text-emerald-600" : "text-slate-500";

  if (isLoading) {
    return (
      <div className={`rounded-md border p-3 ${toneClasses}`}>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-7 w-16" />
        <Skeleton className="mt-2 h-3 w-28" />
      </div>
    );
  }

  const amountNode = (
    <div className="mt-1 text-[11px] font-semibold text-slate-700 tabular-nums">
      {currency} {fmtMoney(total)}
    </div>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group text-left rounded-md border p-3 transition-colors ${toneClasses} ${ringClasses} focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400`}
      aria-label={`Open ${label} tab`}
    >
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={iconTone}>{icon}</span>
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 truncate">
            {label}
          </span>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-transform" />
      </div>
      <div className="mt-2 text-2xl font-black tabular-nums text-slate-900 leading-none">
        {count}
      </div>
      {currency === "Mixed" ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-help">{amountNode}</div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {Array.from(byCurrency.entries()).map(([cur, val]) => (
                <div key={cur} className="tabular-nums">
                  {cur} {fmtMoney(val)}
                </div>
              ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        amountNode
      )}
      <div className="mt-2 text-[10px] font-semibold text-slate-500 group-hover:text-slate-800">
        View details →
      </div>
    </button>
  );
}

function PerBankList({
  title,
  tone,
  rows,
  isLoading,
  onPickBank,
  bankAccountIdFiltered,
}: {
  title: string;
  tone: "bank" | "paid";
  rows: ReturnType<typeof groupByBank>;
  isLoading: boolean;
  onPickBank: (bankId: string) => void;
  bankAccountIdFiltered?: string | null;
}) {
  const [open, setOpen] = React.useState(false);

  const headerTone =
    tone === "bank"
      ? "border-amber-200 bg-amber-50/40 hover:bg-amber-50"
      : "border-rose-200 bg-rose-50/40 hover:bg-rose-50";
  const labelTone = tone === "bank" ? "text-amber-700" : "text-rose-700";

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={`w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 transition-colors ${headerTone}`}
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-slate-600" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-slate-600" />
          )}
          <span className={`text-[10px] font-black uppercase tracking-widest ${labelTone}`}>
            {title}
          </span>
          <span className="text-[11px] text-slate-600">
            ({rows.length} {rows.length === 1 ? "bank" : "banks"})
          </span>
        </div>
        <span className="text-[10px] font-semibold text-slate-500">
          {open ? "Hide" : "Show all banks"}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 rounded-md border border-slate-200 bg-white">
          {isLoading ? (
            <div className="p-3 space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-3 text-[11px] text-slate-500">No data in this range.</div>
          ) : bankAccountIdFiltered ? (
            <div className="p-3 text-[11px] text-slate-500">
              Account filter active — clear it to see per-bank breakdown across all accounts.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => b.id !== "__none__" && onPickBank(b.id)}
                    disabled={b.id === "__none__"}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {b.name}
                      </span>
                      {b.id !== "__none__" && (
                        <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                      )}
                    </div>
                    <div className="text-[11px] text-slate-700 tabular-nums whitespace-nowrap">
                      <span className="font-semibold">{b.count}</span> ·{" "}
                      <span className="font-semibold">
                        {b.displayCurrency} {fmtMoney(b.amount)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ReconciliationSummary({ dateFrom, dateTo, bankAccountId, accounts }: Props) {
  const qUB = useUnmatchedBankTransactions({ dateFrom, dateTo, bankAccountId });
  const qUP = useUnmatchedPaidExpenses({ dateFrom, dateTo, bankAccountId });
  const qMB = useMatchedBankTransactions({ dateFrom, dateTo, bankAccountId });
  const qMP = useMatchedPaidExpenses({ dateFrom, dateTo, bankAccountId });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as TabKey | null) ?? null;

  const goToTab = React.useCallback(
    (tab: TabKey, opts?: { bankId?: string | null }) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      if (opts && "bankId" in opts) {
        if (opts.bankId) next.set("accountId", opts.bankId);
        else next.delete("accountId");
      }
      setSearchParams(next, { replace: true });
      // scroll the matching tabs into view
      requestAnimationFrame(() => {
        const el = document.getElementById("reconciliation-matching-tabs");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [searchParams, setSearchParams]
  );

  const unmatchedBank = qUB.data?.unmatchedBank ?? [];
  const unmatchedPaid = qUP.data?.unmatchedPaid ?? [];
  const matchedBank = qMB.data?.matchedBank ?? [];
  const matchedPaid = qMP.data?.matchedPaid ?? [];

  const sUB = React.useMemo(
    () => summarize(unmatchedBank, ["transaction_amount"], "transaction_amount_currency"),
    [unmatchedBank]
  );
  const sUP = React.useMemo(
    () => summarize(unmatchedPaid, ["total_amount", "amount"], "currency"),
    [unmatchedPaid]
  );
  const sMB = React.useMemo(
    () => summarize(matchedBank, ["transaction_amount"], "transaction_amount_currency"),
    [matchedBank]
  );
  const sMP = React.useMemo(
    () => summarize(matchedPaid, ["total_amount", "amount"], "currency"),
    [matchedPaid]
  );

  const accountNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts) m.set(a.id, a.name);
    return m;
  }, [accounts]);

  const perBankUB = React.useMemo(
    () =>
      groupByBank(
        unmatchedBank,
        ["transaction_amount"],
        "transaction_amount_currency",
        accountNameById
      ),
    [unmatchedBank, accountNameById]
  );

  const perBankUP = React.useMemo(
    () => groupByBank(unmatchedPaid, ["total_amount", "amount"], "currency", accountNameById),
    [unmatchedPaid, accountNameById]
  );

  const filteredAccountName = bankAccountId
    ? accountNameById.get(bankAccountId) ?? bankAccountId
    : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-b border-slate-200">
        <div>
          <div className="text-sm font-semibold text-slate-900">Reconciliation Summary</div>
          <div className="mt-1 text-[11px] text-slate-600">
            Range: <span className="font-semibold text-slate-800">{fmtDate(dateFrom)}</span> →{" "}
            <span className="font-semibold text-slate-800">{fmtDate(dateTo)}</span> · Account:{" "}
            <span className="font-semibold text-slate-800">
              {filteredAccountName ?? "All accounts"}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricTile
          label="Unmatched bank txns"
          count={sUB.count}
          total={sUB.total}
          currency={sUB.displayCurrency}
          byCurrency={sUB.byCurrency}
          icon={<Banknote className="h-3.5 w-3.5" />}
          tone="warn"
          isLoading={qUB.isLoading}
          onClick={() => goToTab("unmatched-bank")}
          active={activeTab === "unmatched-bank"}
        />
        <MetricTile
          label="Unmatched paid expenses"
          count={sUP.count}
          total={sUP.total}
          currency={sUP.displayCurrency}
          byCurrency={sUP.byCurrency}
          icon={<Receipt className="h-3.5 w-3.5" />}
          tone="warn"
          isLoading={qUP.isLoading}
          onClick={() => goToTab("unmatched-paid")}
          active={activeTab === "unmatched-paid"}
        />
        <MetricTile
          label="Matched bank txns"
          count={sMB.count}
          total={sMB.total}
          currency={sMB.displayCurrency}
          byCurrency={sMB.byCurrency}
          icon={<TrendingDown className="h-3.5 w-3.5" />}
          tone="ok"
          isLoading={qMB.isLoading}
          onClick={() => goToTab("matched-bank")}
          active={activeTab === "matched-bank"}
        />
        <MetricTile
          label="Matched paid expenses"
          count={sMP.count}
          total={sMP.total}
          currency={sMP.displayCurrency}
          byCurrency={sMP.byCurrency}
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          tone="ok"
          isLoading={qMP.isLoading}
          onClick={() => goToTab("matched-paid")}
          active={activeTab === "matched-paid"}
        />
      </div>

      {/* Per-bank collapsibles */}
      <div className="px-4 pb-4 space-y-3">
        <PerBankList
          title="Unmatched bank transactions — by bank"
          tone="bank"
          rows={perBankUB}
          isLoading={qUB.isLoading}
          bankAccountIdFiltered={bankAccountId}
          onPickBank={(id) => goToTab("unmatched-bank", { bankId: id })}
        />
        <PerBankList
          title="Unmatched paid expenses — by bank"
          tone="paid"
          rows={perBankUP}
          isLoading={qUP.isLoading}
          bankAccountIdFiltered={bankAccountId}
          onPickBank={(id) => goToTab("unmatched-paid", { bankId: id })}
        />
      </div>
    </div>
  );
}
